import { useUser } from "components/clerk";
import { SignInButton } from "@clerk/clerk-react";
import { SiteSelect } from "components/form/SiteSelect";
import { Box, Button, FormattedCurrency, H4, HStack, ParagraphSmall, Text } from "components/ui";
import { H3, H6, Paragraph, Typography } from "components/ui/Typography";
import { useLocalSearchParams, useNavigation, useRouter, useSegments } from "expo-router";
import React, { useEffect, useMemo, useState } from "react";
import { ActivityIndicator } from "react-native";
import { useGetCartByIdQuery, useGetCartCurrentQuery, usePostCartByIdCheckoutMutation, usePostCartByIdSetcurrentcartMutation, usePutCartByIdMutation } from "store/abcoApi";
import { Value } from "types/select.types";
import { useIsMobile } from "utils/hooks/useIsMobile";
import { useCheckoutResponse } from "utils/hooks/useCheckoutResponse";
import { useDataLayer } from "utils/hooks/useDataLayer";
import LocalDateTime from "components/general/LocalDateTime";
import { getValidationStatus } from "./CartValidation";
import { canBeFulfilled } from "screens/CartCheckoutLayout";
import { AlertCircle, TriangleAlertIcon } from "lucide-react-native";
import { useToken } from "@gluestack-style/react";
import { ShippingType } from "types/constants.type";

// Define a proper enum for variants
enum CartVariant {
	CART = "cart",
}

type CartOrderSummaryProps = {
	variant?: CartVariant;
	noReplaceItems?: Set<string>;
};

// Helper to get step from segments
const getStepFromSegments = (segments: string[]) => {
	// Look for a segment like (step2), (step3), etc.
	const stepSegment = segments.find((seg) => /^\(step\d+\)$/.test(seg));
	if (stepSegment) {
		const match = stepSegment.match(/step(\d+)/);
		if (match) return parseInt(match[1], 10);
	}
	// If the route is /cart/[id], treat as step 1
	return 1;
};

const CartOrderSummary = ({ variant = CartVariant.CART, noReplaceItems }: CartOrderSummaryProps) => {
	const { id } = useLocalSearchParams<{ id: string }>();
	const { data: cart, isSuccess } = id && variant === CartVariant.CART ? useGetCartByIdQuery({ id: parseInt(id) }) : useGetCartCurrentQuery();

	const [isApprover, setIsApprover] = useState(false);
	const [buttonText, setButtonText] = useState("Checkout");
	const [showDeliveryRequired, setShowDeliveryRequired] = useState(false);
	const navigation = useNavigation();
	const router = useRouter();
	const [updateCart, { isLoading: isPutCartLoading }] = usePutCartByIdMutation();
	const [setCurrentCart, { isLoading: isSetCurrentCartLoading }] = usePostCartByIdSetcurrentcartMutation();
	const [checkoutCart, { isLoading: isPostCheckountLoading }] = usePostCartByIdCheckoutMutation();
	const segments = useSegments();
	const currentStep = getStepFromSegments(segments);
	const isMobile = useIsMobile();
	const { isLoaded, isSignedIn, user } = useUser();
	const { setCheckoutResponse, setIsLoading } = useCheckoutResponse();
	const { purchase } = useDataLayer();

	// Get cart lines for validation
	const cartLines = cart?.lines || [];
	const validationStatus = getValidationStatus(cartLines, cart, noReplaceItems, currentStep);
	const resolvedWarningMain = useToken("colors", "warningbright");
	const canEdit = useMemo(() => {
		if (!cart) return false;

		const currentUserId = parseInt(user?.externalId || "0", 10);
		const approvalHistory = cart.approvalHistory || [];

		const hasUserApproved = approvalHistory.some((entry) => entry.user?.id === currentUserId);
		if (hasUserApproved) {
			return false;
		}

		const potentialApprovers = cart.potentialApprover || [];
		if (cart.awaitingApproval && potentialApprovers.length > 0) {
			const isApprover = potentialApprovers.some((approver) => approver.id === currentUserId);
			const isNotOwner = cart.userId !== currentUserId;
			return isApprover && isNotOwner;
		}
		return true;
	}, [cart, user?.externalId]);

	useEffect(() => {
		const buttonTexts = {
			1: "Checkout",
			2: "Continue to payment",
			3: "Submit Order",
		};
		setButtonText(buttonTexts[currentStep as keyof typeof buttonTexts] || "Checkout");
		if (isApprover) {
			setButtonText("Approve and Order");
		}
	}, [currentStep, isApprover]);

	useEffect(() => {
		if (!user?.externalId || !cart) return;

		const currentUserId = parseInt(user.externalId, 10);
		const potentialApprovers = cart.potentialApprover || [];
		const isUserApprover = potentialApprovers.some((x) => x.id === currentUserId) && cart.userId !== currentUserId;
		setIsApprover(isUserApprover);
	}, [user, cart]);

	const submitOrder = async () => {
		if (!cart?.id) return;

		// Clear any previous checkout response to avoid flash of old content
		setCheckoutResponse(null);

		// Set loading state and navigate immediately to step 4
		setIsLoading(true);

		// Navigate to step 4 immediately to show processing
		// @ts-ignore
		navigation.navigate("cart/(step4)/index");

		// The actual API call will be handled on step 4
		try {
			const response = await checkoutCart({ id: cart.id }).unwrap();

			if (response) {
				purchase();

				setCheckoutResponse({
					type: response.type || "",
					id: response.id || 0,
					reason: response.reason || null,
					recordType: response.recordType || null,
				});
			}
		} catch (error) {
			console.error("Order submission failed:", error);
			// You could add error handling here, like showing an error message
		} finally {
			// Always set loading to false when done
			setIsLoading(false);
		}
	};

	// const updateSite = (site: Value) => {
	// 	if (!cart?.id) return;
	// 	updateCart({
	// 		cart: {
	// 			siteId: site.id ?? 0,
	// 		},
	// 		id: cart.id,
	// 	});
	// };
	const updateSite = (site: Value) => {
		updateCart({
			cart: {
				// @ts-ignore
				siteId: site.siteId == null ? 0 : site.siteId,
			},
			id: cart?.id as number,
		});
	};

	const handlePress = () => {
		if (currentStep === 2) {
			if (cart?.shippingType === "Delivery" && !cart?.siteId) {
				setShowDeliveryRequired(true);
				return;
			}
			setShowDeliveryRequired(false);
		}

		if (currentStep === 3 || isApprover) {
			submitOrder();
		} else {
			// Always pass the cart ID if available
			if (id) {
				// @ts-ignore
				navigation.navigate(`cart/(step${currentStep + 1})/index`, { id });
			} else {
				// @ts-ignore
				navigation.navigate(`cart/(step${currentStep + 1})/index`);
			}
		}
	};

	const handleSetAsCurrent = () => {
		if (!cart?.id) return;

		setCurrentCart({
			id: cart.id,
		}).then(() => {
			router.push("cart/");
		});
	};

	if (!cart) return null;
	const isButtonDisabled = (
			isPutCartLoading ||
			isPostCheckountLoading ||
			(cart.shippingType ===  ShippingType.Pickup && !cart.pickupLocationId && currentStep !== 1) ||
			(cart.shippingType !== ShippingType.Pickup && currentStep === 2 && !cart.siteId) ||
			(cart.paymentOption === "CREDITCARD" && currentStep === 3 && !cart.paymentTokenId) ||
			(cart.lines && cart.lines.length === 0) ||
			validationStatus.hasBlockingIssues ||
			(cart.requiresPurchaseOrderNumber && !cart.purchaseOrderNumber && currentStep === 2) ||
			(cart.requiresSite && !cart.siteId)
		) &&
		(
			!isApprover
		)
		
		
		;
	const getWarningMessage = () => {
		if( isApprover )
		{
			return null;
		}

		if (validationStatus.hasBlockingIssues) {
			if (validationStatus.status60Count > 0 && validationStatus.status50Count > 0) {
				return {
					title: "Action required to continue.",
					description: "Remove discontinued items and resolve quantity issues to continue.",
				};
			}
			if (validationStatus.status60Count > 0 && validationStatus.unresolvedStatus40Count > 0 && currentStep === 1) {
				return {
					title: "Action required to continue.",
					description: "Remove discontinued items and resolve special order items to continue.",
				};
			}
			if (validationStatus.status60Count > 0) {
				return {
					title: "Action required to continue.",
					description: "Remove discontinued items to continue.",
				};
			}
			if (validationStatus.status50Count > 0) {
				return {
					title: "Action required to continue.",
					description: "Resolve quantity issues to continue.",
				};
			}
			if (validationStatus.unresolvedStatus40Count > 0 && currentStep === 1) {
				return {
					title: "Action required to continue.",
					description: "Please mark special order items as 'do not replace' to continue.",
				};
			}
		}

		if (cart.shippingType === ShippingType.Pickup && !cart.pickupLocationId && currentStep !== 1) {
			return {
				title: "Please select a pickup location to continue.",
				description: "We noticed that you haven't chosen a pickup location yet. Selecting the appropriate pickup location helps us provide accurate information.",
			};
		}
		if (currentStep === 2 && cart.shippingType !== ShippingType.Pickup && !cart.siteId) {
			return {
				title: "Please select a site to continue.",
				description: "We noticed that you haven't chosen a site yet. Selecting the appropriate site helps us provide accurate information.",
			};
		}
		if (cart.paymentOption === "CREDITCARD" && currentStep === 3 && !cart.paymentTokenId) {
			return {
				title: "Please select a payment card to continue.",
				description: "A payment method is required to complete your order.",
			};
		}
		if (cart.requiresPurchaseOrderNumber && !cart.purchaseOrderNumber && currentStep === 2) {
			return {
				title: "Please enter a PO number to continue.",
				description: "A purchase order number is required for this transaction.",
			};
		}
		if (cart.requiresSite && !cart.siteId) {
			return {
				title: "Please select a site to continue.",
				description: "A site is required for this order.",
			};
		}
		return null;
	};

	const getButtonText = () => {
		return buttonText;
	};

	if (isMobile) {
		const warningMessage = getWarningMessage();
		return (
			<Box w="$full" borderWidth={1} borderTopLeftRadius={12} borderTopRightRadius={12} borderColor="black" paddingVertical={24} paddingHorizontal={16} gap={16}>
				<Box>
					<ParagraphSmall weight="normal">Cart total:</ParagraphSmall>
					<FormattedCurrency
						sx={{
							fontSize: 28,
							fontWeight: "bold",
						}}
						amount={cart?.totalAmount}
					/>
					<ParagraphSmall weight="normal">Excluding delivery charges & fees </ParagraphSmall>
				</Box>
				{warningMessage && (
					<Box flexDirection="row" gap={8} borderRadius={6} borderWidth={1} borderColor="$warningbright" bg="$warningdisabled" paddingVertical={8} paddingHorizontal={12}>
						<TriangleAlertIcon size={16} color={resolvedWarningMain} />
						<Box paddingRight={4} flex={1}>
							<ParagraphSmall fontSize="$xs">
								<Text style={{ fontFamily: "Roboto_700Bold" }}>{warningMessage.title} </Text>
								<Text>{warningMessage.description}</Text>
							</ParagraphSmall>
						</Box>
					</Box>
				)}
				<Button bg={isButtonDisabled ? "$neutral25" : "$secondary"} marginBottom={5} borderRadius={8} p={12} disabled={isButtonDisabled} onPress={handlePress}>
					{(isPutCartLoading || isPostCheckountLoading) && <ActivityIndicator style={{ position: "absolute", right: 10 }} />}
					<Typography.Paragraph weight="bold">{getButtonText()}</Typography.Paragraph>
				</Button>
			</Box>
		);
	}
	return (
		<Box
			borderBottomLeftRadius={12}
			borderBottomRightRadius={12}
			flex={1}
			sx={{
				// @ts-ignore
				height: "fit-content",
				maxWidth: isMobile ? "$full" : 302,
				// @ts-ignore
				position: "sticky",
				top: 100,
			}}
			bg={isMobile ? "$basicwhite" : "$neutral25"}
			width={isMobile ? "$full" : 302}
			borderWidth={1}
			borderColor="$neutral50"
		>
			{!isMobile && (
				<Box borderTopLeftRadius={12} borderTopRightRadius={12} paddingHorizontal={16} paddingVertical={20} bg="$primary">
					<H4 sx={{ color: "$basicwhite" }}>Order summary</H4>
				</Box>
			)}

			<Box padding={12}>
				{isMobile && <H4 sx={{ color: "$basicblack" }}>Order summary</H4>}

				{cart.id !== 0 && (
					<>
						{(cart?.approvalHistory && cart?.approvalHistory.length > 0) || cart.hasCompleted ? (
							<Box gap={12} paddingHorizontal={4} paddingVertical={16} borderBottomWidth={1} borderColor="$neutral100">
								<ParagraphSmall weight="normal">
									Cart number: <Typography weight="bold">{cart.id}</Typography>
								</ParagraphSmall>
								<ParagraphSmall weight="normal">
									Site: <Typography weight="bold">{cart.siteName ?? "N/A"}</Typography>
								</ParagraphSmall>
								<ParagraphSmall weight="normal">
									Order Date:{" "}
									<Typography weight="bold">
										<LocalDateTime utcDate={cart.dateCreated} />
									</Typography>
								</ParagraphSmall>
							</Box>
						) : (
							<Box paddingHorizontal={isMobile ? 0 : 4} paddingTop={isMobile ? 0 : 16} paddingBottom={16} borderBottomWidth={1} borderColor="$neutral100">
								<ParagraphSmall weight="normal" sx={{ paddingBottom: 24 }}>
									Cart number: <Text fontWeight="bold">{cart.id}</Text>
								</ParagraphSmall>
								<SiteSelect disabled={cart.awaitingApproval} showError={showDeliveryRequired} value={{ id: cart.siteId ?? 0, name: cart.siteName ?? "" }} onSelect={updateSite} allowedTypes="Site" />
								{/* <SiteSelect disabled={cart.awaitingApproval} showError={showDeliveryRequired} value={{ id: cart.siteId ?? 0, name: cart.siteName ?? "" }} onSelect={updateSite} allowedTypes="Site" /> */}
							</Box>
						)}
					</>
				)}

				<Box paddingHorizontal={4} paddingVertical={16} borderBottomWidth={1} borderColor="$neutral100">
					<HStack justifyContent="space-between">
						<Box>
							<H4 sx={{ fontSize: isMobile ? 16 : 14 }}>Sub total:</H4>
						</Box>
						<FormattedCurrency amount={cart.itemTotalAmount} component={H4} />
					</HStack>

					{cart.id !== 0 && (
						<HStack justifyContent="space-between">
							<Box>
								<ParagraphSmall sx={{ fontSize: isMobile ? 16 : 14 }} weight={isMobile ? "bold" : "normal"}>
									Shipping:
								</ParagraphSmall>
							</Box>
							<FormattedCurrency amount={cart.freightCharge} zeroIsFree={true} component={ParagraphSmall} />
						</HStack>
					)}

					{cart && cart.creditCardSurchargeCost && cart.creditCardSurchargeCost > 0 && (
						<HStack justifyContent="space-between">
							<Box>
								<ParagraphSmall sx={{ fontSize: isMobile ? 16 : 14 }} weight={isMobile ? "bold" : "normal"}>
									Credit Card Surcharge:
								</ParagraphSmall>
							</Box>
							<FormattedCurrency amount={cart.creditCardSurchargeCost} component={ParagraphSmall} />
						</HStack>
					)}

					<HStack justifyContent="space-between">
						<Box>
							<ParagraphSmall sx={{ fontSize: isMobile ? 16 : 14 }} weight={isMobile ? "bold" : "normal"}>
								GST:
							</ParagraphSmall>
						</Box>
						<FormattedCurrency amount={cart.taxAmount} component={ParagraphSmall} />
					</HStack>
				</Box>

				<Box paddingHorizontal={4} paddingVertical={16}>
					<HStack justifyContent="space-between">
						<Box>
							<H6>Total:</H6>
							<ParagraphSmall weight="normal">Incl. GST</ParagraphSmall>
						</Box>
						<FormattedCurrency sx={{ fontSize: 28, lineHeight: 32 }} amount={cart.totalAmount} component={H3} />
					</HStack>
				</Box>
				{(() => {
					const warningMessage = getWarningMessage();
					return warningMessage ? (
						<Box flexDirection="row" gap={12} borderRadius={6} borderWidth={1} borderColor="$warningbright" bg="$warningdisabled" paddingVertical={6} paddingHorizontal={8} mb={24}>
							<TriangleAlertIcon size={18} color={resolvedWarningMain} />
							<Box paddingRight={6} flex={1}>
								<ParagraphSmall>
									<Text style={{ fontFamily: "Roboto_700Bold" }}>{warningMessage.title} </Text>
									<Text>{warningMessage.description}</Text>
								</ParagraphSmall>
							</Box>
						</Box>
					) : null;
				})()}

				{isSignedIn && cart.lines && cart.lines.length > 0 && !cart.hasCompleted && canEdit && (
					<Button sx={{ cursor: isButtonDisabled ? "not-allowed" : "pointer" }} disabled={isButtonDisabled} bg={isButtonDisabled ? "$neutral100" : "$secondary"} gap={8} variant="secondary" onPress={handlePress}>
						{isPostCheckountLoading && <ActivityIndicator size="small" color="black" />}
						<Paragraph weight="bold" color={isButtonDisabled ? "$neutral500" : "$basicblack"}>
							{isPostCheckountLoading ? "Processing..." : getButtonText()}
						</Paragraph>
					</Button>
				)}

				{!isSignedIn && cart.lines && cart.lines.length > 0 && !cart.hasCompleted && canEdit && (
					<Button variant="secondary">
						<SignInButton>
							<Paragraph weight="bold">Please sign in to continue</Paragraph>
						</SignInButton>
					</Button>
				)}

				{!cart.hasCompleted && canEdit && id && !cart.isCurrentCart && (
					<Button variant="primary-light" onPress={handleSetAsCurrent} mt={12} borderRadius={12}>
						{(isPutCartLoading || isPostCheckountLoading) && <ActivityIndicator style={{ position: "absolute", right: 10 }} />}
						<Paragraph color="$basicwhite">Set as current cart</Paragraph>
					</Button>
				)}
			</Box>
		</Box>
	);
};

export default CartOrderSummary;
